import transform from 'lodash/transform';
import isEqual from 'lodash/isEqual';
import isObject from 'lodash/isObject';
import reduce from 'lodash/reduce';
import md5 from 'md5';
import { i18n } from '@/plugins/vue-i18n';

/**
 * Return a delayed promise
 * @return {Promise} Delayed promise
 */
export async function delay(time) {
  return new Promise((fulfill) => {
    setTimeout(fulfill, time);
  });
}

/**
 * Deep diff between two object, using lodash
 * @param  {Object} object Object compared
 * @param  {Object} base   Object to compare with
 * @return {Object}        Return a new object who represent the diff
 */
export function difference(object, base) {
  function changes(_object, _base) {
    return transform(_object, (result, value, key) => {
      if (!isEqual(value, _base[key])) {
        result[key] = (isObject(value) && isObject(_base[key]))
          ? changes(value, _base[key]) : value;
      }
    });
  }

  return changes(object, base);
}

/**
 * Modified object properties, using lodash
 * @param  {Object} object Object compared
 * @param  {Object} base   Object to compare with
 * @return {Array}        Return a new object who represent the diff
 */
export function modifiedProperties(object, base) {
  return reduce(object, (result, value, key) => (isEqual(value, base[key]) ? result : result.concat(key)), []);
}

/**
 * Returns the given parameter as an array.
 * @param {*} param
 * @returns {[]} Returns the given parameter ensuring that is an array.
 */
export function toArray(param) {
  if (param === null
    || param === undefined) {
    return [];
  }
  if (Array.isArray(param)) {
    return param;
  }
  return [param];
}

/**
 * Scroll animation function.
 * @param {Number} scrollTargetY Y coordinate to scroll to.
 * @param {Number} speed         Animation speed.
 * @param {String} easing        Easing function to apply to the animation.
 */
export const animateScroll = (() => {
  const easingEquations = {
    easeOutSine(pos) {
      return Math.sin(pos * (Math.PI / 2));
    },
    easeInOutSine(pos) {
      return (-0.5 * (Math.cos(Math.PI * pos) - 1));
    },
    easeInOutQuint(pos) {
      pos /= 0.5;
      if (pos < 1) {
        return 0.5 * (pos ** 5);
      }
      return 0.5 * (((pos - 2) ** 5) + 2);
    },
  };

  return (scrollTargetY = 0, speed = 2000, easing = 'easeOutSine') => {
    easing = Object.keys(easingEquations).includes(easing) ? easing : 'easeOutSine';

    const easingEquation = easingEquations[easing];
    const scrollY = window.scrollY;
    const time = Math.max(0.1, Math.min(Math.abs(scrollY - scrollTargetY) / speed, 0.8));

    let currentTime = 0;
    const tick = () => {
      currentTime += (1 / 60);
      const p = currentTime / time;
      const t = easingEquation(p);

      if (p < 1) {
        requestAnimationFrame(tick);
        window.scrollTo(0, scrollY + ((scrollTargetY - scrollY) * t));
      } else {
        window.scrollTo(0, scrollTargetY);
      }
    };
    requestAnimationFrame(tick);
    // tick();
  };
})();

/**
 * Check if two dom elements overlaps
 * @param {Element} elem1
 * @param {Element} elem2
 */
export function overlaps(elem1, elem2) {
  function getPositions(elem) {
    const pos = elem.getBoundingClientRect();
    return [[pos.left, pos.right], [pos.top, pos.bottom]];
  }

  function comparePositions(p1, p2) {
    const r1 = p1[0] < p2[0] ? p1 : p2;
    const r2 = p1[0] < p2[0] ? p2 : p1;
    return r1[1] > r2[0] || r1[0] === r2[0];
  }

  const pos1 = getPositions(elem1);
  const pos2 = getPositions(elem2);
  return comparePositions(pos1[0], pos2[0]) && comparePositions(pos1[1], pos2[1]);
}

/**
 * Moves an array item from one position to another.
 * @param  {Array}  array
 * @param  {Number} fromIndex
 * @param {Number} toIndex
 * @return {Array}
 */
export function move(array, fromIndex, toIndex) {
  const item = array[fromIndex];
  const length = array.length;
  const diff = fromIndex - toIndex;

  if (diff > 0) {
    // move left
    return [
      ...array.slice(0, toIndex),
      item,
      ...array.slice(toIndex, fromIndex),
      ...array.slice(fromIndex + 1, length),
    ];
  }
  if (diff < 0) {
    // move right
    return [
      ...array.slice(0, fromIndex),
      ...array.slice(fromIndex + 1, toIndex),
      item,
      ...array.slice(toIndex, length),
    ];
  }
  return array;
}

/**
 * Calculates the cartesian product between N arrays
 * https://stackoverflow.com/questions/46951223/generation-cartesian-product-objects-from-array-values
 * https://es.wikipedia.org/wiki/Producto_cartesiano
 */
export function cartesianProduct(...arrays) {
  return [...arrays].reduce((a, b) => a.map(x => b.map(y => x.concat(y)))
    .reduce((x, y) => x.concat(y), []), [[]]);
}

/**
 * Flatten an array of objects
 * From:
 [
 { type: 'article' },
 { taxonomyId: 27 },
 ]
 *
 * To: [
 *     type: 'article'
 *     taxonomyId: 27
 * ]
 *
 * @param  {Array}  arrayOfObjects
 * @return {Array}
 */
export function flattenArrayOfObjects(arrayOfObjects) {
  return arrayOfObjects.reduce((result, value) => ({ ...result, ...value }), {});
}

/**
 * Generate a unique hash from an object
 * @param {{}} objectToHash
 * @return string
 */
export function objectHash(objectToHash) {
  function sortObjectByKey(obj) {
    return Object.keys(obj)
      .sort()
      .reduce((acc, key) => {
        let item = obj[key];
        if (isObject(item)) {
          item = sortObjectByKey(item);
        }
        if (Number.parseInt(item, 10)) {
          item = Number.parseInt(item, 10);
        }
        acc[key] = item;
        return acc;
      }, {});
  }

  return md5(JSON.stringify(sortObjectByKey(objectToHash)));
}

export function entityMetadata(entityName) {
  const metadata = {
    navigation: {
      id: null,
      name: null,
      modificationDate: ['formatDate'],
      creationDate: ['formatDate'],
    },
    staff: {
      id: null,
      name: null,
      surname: null,
      nick: null,
      email: null,
      description: null,
      creationDate: ['formatDate'],
      modificationDate: ['formatDate'],
    },
    section: {
      id: null,
      name: null,
      type: ['trad'],
      hasChildren: ['?'],
      creationDate: ['formatDate'],
      modificationDate: ['formatDate'],
    },
    microsite: {
      id: null,
      name: null,
    },
  };
  return metadata[entityName] || null;
}

/**
 * Sorts the properties of the given object alphabetically.
 * @param {{}}      obj
 * @param {boolean} recursive
 */
export function sortObjectPropertiesByKey(obj, recursive = true) {
  const ordered = {};
  if (typeof obj !== 'object'
    || obj === null) {
    return ordered;
  }
  Object.keys(obj).sort().forEach((key) => {
    const value = obj[key];
    ordered[key] = ((typeof value === 'object') && recursive)
      ? sortObjectPropertiesByKey(value)
      : value;
  });
  return ordered;
}

export function $t(path, defaultValue = '') {
  return i18n.te(path) ? i18n.t(path) : defaultValue;
}

/**
 * Translate and return the first match in an array of ${translationCandidates} strings
 * if no one is a valid translation return ${missingTranslationText}
 * @param {string[]} translationCandidates
 * @param {string} missingTranslationText
 * @param {array} translationVars
 * @returns {string}
 */
export function translateCandidates(translationCandidates, missingTranslationText = '', translationVars = []) {
  const translation = translationCandidates.find(candidate => i18n.te(candidate));
  if (translation) {
    return i18n.t(translation, translationVars);
  }
  return missingTranslationText;
}

/**
 * Reduces an array into an object, indexing each object in the array by the given `keyField`.
 * @param array
 * @param keyField
 * @return {*}
 */
export const transpose = (array, keyField) => array.reduce((map, item) => {
  item[keyField] = item[keyField].toString();
  const key = item[keyField];
  map[key] = item;
  return map;
}, {});

/**
 * Transform a slug string to a readable one
 * @param slug string
 * @return string
 */
export function unslug(slug) {
  const parts = slug.replace(/(_|--)/g, '-').split('-');
  return parts.join(' ');
}

export const OPERATORS = {
  EQUAL: '==',
  STRICT_EQUAL: '===',
  NOT_EQUAL: '!=',
  STRICT_NOT_EQUAL: '!==',
  GREATER_THAN: '>',
  GREATER_THAN_OR_EQUAL: '>=',
  LESS_THAN: '<',
  LESS_THAN_OR_EQUAL: '<=',
  IN: 'in',
  NOT_IN: 'not in',
};

export function compareWithOperator(value, expected, operator) {
  switch (operator) {
    case OPERATORS.EQUAL:
      // noinspection EqualityComparisonWithCoercionJS
      return (expected === true)
        ? Boolean(value) // If expected value is `true`, return if value is truthy.
        // eslint-disable-next-line eqeqeq
        : value == expected;
    case OPERATORS.STRICT_EQUAL:
      return value === expected;
    case OPERATORS.NOT_EQUAL:
      // eslint-disable-next-line eqeqeq
      return value != expected;
    case OPERATORS.STRICT_NOT_EQUAL:
      return value !== expected;
    case OPERATORS.GREATER_THAN:
      return value > expected;
    case OPERATORS.GREATER_THAN_OR_EQUAL:
      return value >= expected;
    case OPERATORS.LESS_THAN:
      return value < expected;
    case OPERATORS.LESS_THAN_OR_EQUAL:
      return value <= expected;
    case OPERATORS.IN:
      return Array.isArray(expected) ? expected.includes(value) : [expected].includes(value);
    case OPERATORS.NOT_IN:
      return Array.isArray(expected) ? !expected.includes(value) : ![expected].includes(value);
    default:
      console.error('Error while comparing values: '
        + 'the given comparator is not valid', {
        value,
        expected,
        operator,
      });
      // eslint-disable-next-line eqeqeq
      return value == expected;
  }
}

export function operatorToString(operator) {
  switch (operator) {
    case OPERATORS.EQUAL:
      return 'equ';
    case OPERATORS.STRICT_EQUAL:
      return 'str_equ';
    case OPERATORS.NOT_EQUAL:
      return 'neq';
    case OPERATORS.STRICT_NOT_EQUAL:
      return 'str_neq';
    case OPERATORS.GREATER_THAN:
      return 'gt';
    case OPERATORS.GREATER_THAN_OR_EQUAL:
      return 'gt_eq';
    case OPERATORS.LESS_THAN:
      return 'lt';
    case OPERATORS.LESS_THAN_OR_EQUAL:
      return 'lt_eq';
    case OPERATORS.IN:
      return 'in';
    case OPERATORS.NOT_IN:
      return 'not_in';
    default:
      console.error('Operator does not exist', {
        operator,
      });
      return '';
  }
}

export function getScrollableParent(node) {
  if (node == null) {
    return null;
  }
  if (node.scrollHeight > node.clientHeight) {
    return node;
  }
  return getScrollableParent(node.parentNode);
}

export function elementInViewport(element) {
  const rect = element.getBoundingClientRect();
  return (
    rect.top >= 0
    && rect.left >= 0
    && rect.bottom <= (window.innerHeight || document.documentElement.clientHeight)
    && rect.right <= (window.innerWidth || document.documentElement.clientWidth) /* or $(window).width() */
  );
}

export async function copyToClipboard(text) {
  if (!navigator.clipboard) {
    console.warn('Clipboard API not supported');
    // Se crea un textarea no visible para pegar y copiar el texto deseado
    const textarea = document.createElement('textarea');
    textarea.value = text;

    // Avoid scrolling to bottom
    textarea.style.top = '0';
    textarea.style.left = '0';
    textarea.style.position = 'fixed';

    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();

    let success = false;
    try {
      success = document.execCommand('copy');
    } catch (err) {
      console.error('Fallback: Oops, unable to copy', err);
    }

    // Se destruye el elemento auxiliar
    textarea.blur();
    document.body.removeChild(textarea);

    if (!success) throw new Error('Copy operation failed');
    return;
  }

  try {
    await navigator.clipboard.writeText(text);
  } catch (error) {
    throw new Error(error);
  }
}

export function elementHasFixedOrStickyPosition(element) {
  let currentElement = element;
  while (currentElement) {
    const position = window.getComputedStyle(currentElement).position;
    if (position === 'fixed' || position === 'sticky') {
      return true;
    }
    currentElement = currentElement.parentElement;
  }
  return false;
}

export function mimeTypeMatch(mime, accepted) {
  const allowed = accepted.split(',').map(x => x.trim());
  return allowed.includes(mime) || allowed.includes(`${mime.split('/')[0]}/*`) || accepted === '*';
}
